import { assignInlineVars } from '@vanilla-extract/dynamic'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import type { Key } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAvatar, ConnectedFeatureTooltip } from '@src/app/components'
import useFeatureTooltipsActions from '@src/app/components/FeatureTooltip/useFeatureTooltipsActions'
import type { AvatarStatusBadge } from '@src/app/components/app-avatar/AppAvatar'
import { useAppStore } from '@src/app/context'
import { useCurrentUser } from '@src/app/user'
import { useCallsDialogContext } from '@src/app/voice/context/useCallsDialogContext'
import { useCallContext } from '@src/app/voice/ongoing-call/context/useCallContext'
import Button from '@src/component/ButtonV2'
import IconButton from '@src/component/IconButton'
import useTransition from '@src/lib/hooks/useTransition'
import isNonNull from '@src/lib/isNonNull'
import { formatted } from '@src/lib/phone-number'
import type { Identity } from '@src/service/model'
import { PhoneNumberModel, isIdentity } from '@src/service/model'
import type { ActiveCallParticipant } from '@src/service/voice'
import type ActiveCallParticipants from '@src/service/voice/ActiveCallParticipants'
import { LoadingIndicator } from '@ui/LoadingIndicator'
import Menu from '@ui/Menu'
import Popover from '@ui/Popover'
import TextWrap from '@ui/TextWrap'
import Tooltip from '@ui/Tooltip'
import Typography from '@ui/Typography'
import VisuallyHidden from '@ui/VisuallyHidden'
import { WhisperIcon } from '@ui/icons/tint/14/general'
import { XIcon } from '@ui/icons/tint/16/general'
import {
  CallEndIcon,
  CallHoldIcon,
  CallLeaveIcon,
  CallMicMuteIcon,
  ContactCardIcon,
  MoreIcon,
} from '@ui/icons/tint/20/general'
import { theme } from '@ui/theme'
import type ThemeContract from '@ui/theme/ThemeContract'
import { LayerProvider } from '@ui/z-index'

import ParticipantInfo from './ParticipantInfo'
import * as styles from './ParticipantRow.css'

const getInvitedBy = (
  participant: ActiveCallParticipant,
  callParticipants: ActiveCallParticipants,
) => {
  if (participant === callParticipants.from) {
    return 'Initiated the call'
  }

  if (participant.invitation?.fromParticipantId) {
    const fromParticipant = callParticipants.list.find(
      (p) => p.id === participant.invitation?.fromParticipantId,
    )
    if (fromParticipant) {
      return `Added by ${fromParticipant.name}`
    }
  }

  return null
}

const getSubtitle = (participant: ActiveCallParticipant) => {
  if (!participant || participant.status === 'ringing') {
    return null
  }

  if (participant.isCurrentUser) {
    return 'You'
  }

  if (participant.isTeamMember) {
    return 'Team member'
  }

  return participant.name
}

const getTitle = (participant: ActiveCallParticipant) => {
  if (participant.status === 'ringing') {
    if (participant.phoneNumber?.isShared && !participant.userId) {
      return `Connecting to someone in ${participant.phoneNumber?.name}...`
    }

    return `Connecting to ${participant.name}`
  }

  return participant.name
}

interface ParticipantRowProps {
  participant: ActiveCallParticipant
}

const ParticipantRow = ({ participant }: ParticipantRowProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const popoverState = useTransition(
    isPopoverOpen,
    {
      enterDelay: 550,
      enterDuration: 0,
      exitDelay: 0,
      exitDuration: 0,
    },
    [isPopoverOpen],
  )
  const rootRef = useRef<HTMLDivElement | null>(null)
  const moreRef = useRef<HTMLButtonElement>(null)
  const { toast, service } = useAppStore()
  const { call } = useCallContext()
  const user = useCurrentUser()
  const { blurred } = useCallsDialogContext()
  const navigate = useNavigate()
  const { dismiss } = useFeatureTooltipsActions()
  const holdCapabilityEnabled = service.capabilities.features.callHoldingEnabled
  const shouldAnimate =
    (participant.invitation?.type === 'added' ||
      participant.invitation?.type === 'transferred') &&
    (!participant.addedAt || Date.now() - participant.addedAt?.getTime() < 5000)
  const shouldShowPopover =
    // Popovers are currently descoped due to inaccurate informations caused by the unified participants list
    // https://linear.app/openphone/issue/VOICE-8103/remove-metadata-tooltips-from-group-calling
    false && popoverState !== 'enter' && popoverState !== 'exited' && isPopoverOpen
  const invitedBy = useMemo(
    () => getInvitedBy(participant, call.participants),
    [participant, call.participants],
  )
  const title = useMemo(() => getTitle(participant), [participant])
  const subtitle = useMemo(() => getSubtitle(participant), [participant])
  const isFirstExternal =
    call.participants.externals.findIndex((p) => p === participant) === 0

  const sendCallParticipantHoldToggledAnalytics = useCallback(
    (value: boolean) => {
      service.analytics.voice.callParticipantHoldToggled(
        value,
        participant.isContact ? 'contact' : 'number',
      )
    },
    [participant.isContact, service.analytics],
  )

  const handleCancel = () => {
    call.participants
      .remove(participant.id)
      .catch(() => toast.showError(new Error('An error has occured. Please try again.')))
  }

  // const handleRetry = () => {
  //   call.participants.retryAdd(participant.id).catch(toast.showError)
  // }

  const handlePutParticipantOnHold = () => {
    dismiss('participantHold1', 'participantHold2')
    sendCallParticipantHoldToggledAnalytics(true)
    call.participants.update(participant.id, { onHold: true }).catch(toast.showError)
    setIsMenuOpen(false)
  }

  const handleTakeParticipantOffHold = () => {
    sendCallParticipantHoldToggledAnalytics(false)
    call.participants.update(participant.id, { onHold: false }).catch(toast.showError)
    setIsMenuOpen(false)
  }

  const handleMuteParticipant = () => {
    call.participants.update(participant.id, { isMuted: true }).catch(toast.showError)
    setIsMenuOpen(false)
  }

  const handleUnmuteParticipant = () => {
    call.participants.update(participant.id, { isMuted: false }).catch(toast.showError)
    setIsMenuOpen(false)
  }

  const handleRemove = () => {
    service.analytics.voice.callParticipantRemoved(
      call.participants.list.length,
      call.participants.list.length - 1,
      participant.isTeamMember
        ? 'member'
        : participant.isInbox
        ? 'inbox'
        : participant.isContact
        ? 'contact'
        : 'number',
    )
    call.participants.remove(participant.id).catch(toast.showError)
    setIsMenuOpen(false)
  }

  const handleMore = () => {
    setIsPopoverOpen(false)
    setIsMenuOpen(!isMenuOpen)
  }

  const handleStartWhispering = () => {
    if (call.participants.current) {
      call.participants
        .update(call.participants.current.id, { coaching: participant.id })
        .catch(toast.showError)
      setIsMenuOpen(false)
    }
  }

  const handleStopWhispering = () => {
    if (call.participants.current) {
      call.participants
        .update(call.participants.current.id, { coaching: undefined })
        .catch(toast.showError)
      setIsMenuOpen(false)
    }
  }

  const handleViewContactInfo = () => {
    const contact = participant.contact
    if (!contact) {
      return
    }

    navigate(`/contacts/${contact.id}`)
    setIsMenuOpen(false)
  }

  const onAction = (key: Key) => {
    switch (key) {
      case 'stopWhispering':
        handleStopWhispering()
        break
      case 'startWhispering':
        handleStartWhispering()
        break
      case 'takeParticipantOffHold':
        handleTakeParticipantOffHold()
        break
      case 'putParticipantOnHold':
        handlePutParticipantOnHold()
        break
      case 'unmute':
        handleUnmuteParticipant()
        break
      case 'mute':
        handleMuteParticipant()
        break
      case 'viewContactInfo':
        handleViewContactInfo()
        break
      case 'remove':
        handleRemove()
        break
    }
  }

  const menuItems = (
    call.isListener
      ? [
          !participant.isListener ? (
            call.participants.current?.coaching === participant ? (
              <Menu.ListItem key="stopWhispering" icon={<WhisperIcon />}>
                Stop whispering
              </Menu.ListItem>
            ) : (
              <Menu.ListItem key="startWhispering" icon={<WhisperIcon />}>
                Start whispering
              </Menu.ListItem>
            )
          ) : null,
        ]
      : [
          holdCapabilityEnabled &&
          !participant.isCurrentUser &&
          participant.isExternal ? (
            participant.onHold ? (
              <Menu.ListItem key="takeParticipantOffHold" icon={<CallHoldIcon />}>
                Take off hold
              </Menu.ListItem>
            ) : (
              <Menu.ListItem
                key="putParticipantOnHold"
                icon={<CallHoldIcon />}
                textValue="Put on hold"
              >
                <LayerProvider layer="callDialog">
                  <ConnectedFeatureTooltip
                    name={'participantHold2'}
                    showForUsersCreatedBefore="2023-08-24"
                    shouldCloseOnScroll={false}
                    shouldCloseOnInteractOutside={false}
                    isDisabled={!user.settings.web.newFeatureTooltips.participantHold1}
                    autoFocus={false}
                    title="It’s right here"
                    description='To put an individual participant on hold, navigate to their name, open the menu, and click "Put on hold"'
                    placement="left"
                    offset={65}
                    onClose={() => {
                      dismiss('participantHold2', 'participantHold3')
                    }}
                    actions={[
                      {
                        type: 'primary',
                        title: 'Got it',
                        onAction: () => {
                          dismiss('participantHold2')
                          setIsMenuOpen(false)
                        },
                      },
                    ]}
                  >
                    <span>Put on hold</span>
                  </ConnectedFeatureTooltip>
                </LayerProvider>
              </Menu.ListItem>
            )
          ) : null,
          false ? (
            participant.isMuted ? (
              <Menu.ListItem key="unmute" icon={<CallMicMuteIcon />}>
                Unmute mic
              </Menu.ListItem>
            ) : (
              <Menu.ListItem key="mute" icon={<CallMicMuteIcon />}>
                Mute mic
              </Menu.ListItem>
            )
          ) : null,
          participant.isContact ? (
            <Menu.ListItem key="viewContactInfo" icon={<ContactCardIcon />}>
              View contact info
            </Menu.ListItem>
          ) : null,
          participant !== call.participants.from ? (
            call.isGroupCall ? (
              <Menu.ListItem key="remove" icon={<CallLeaveIcon />}>
                {participant.isCurrentUser ? 'Leave call' : 'Remove from call'}
              </Menu.ListItem>
            ) : (
              <Menu.ListItem
                key="remove"
                icon={
                  <CallEndIcon color={`rgb(${theme.palette.secondary.red.default})`} />
                }
              >
                {participant.isCurrentUser ? 'Leave & end call' : 'Remove & end call'}
              </Menu.ListItem>
            )
          ) : null,
        ]
  ).filter(isNonNull)

  const showShowActionButtons =
    isHovered ||
    isMenuOpen ||
    participant.status === 'failed' ||
    participant.status === 'no-answer' ||
    participant.status === 'busy'

  const shouldShowMoreActionsMenu =
    menuItems.length > 0 &&
    !participant.isCurrentUser &&
    !participant.isListener &&
    (!call.isListener || service.flags.flags.groupCallingWhispering)

  const name = participant.isSuggestedContact
    ? // In case of a suggested contact we want to show the phone number
      formatted(participant.identifier)
    : `${participant.name}${
        participant.phoneNumber?.isShared && !participant.isInbox
          ? ` ${participant.phoneNumber?.symbol}`
          : ''
      }`

  const viaSuffix = participant.via
    ? `via ${
        participant.via instanceof PhoneNumberModel
          ? `${participant.via.symbol} ${participant.via.name}`
          : participant.via.name
      }`
    : null

  const participantBadge =
    participant.isListener && !participant.coaching
      ? undefined
      : participant.onHold
      ? 'on-hold'
      : participant.isMuted
      ? 'muted'
      : undefined

  const participantStatus = (({
    badge,
    identity,
  }: { badge?: AvatarStatusBadge; identity?: Identity } = {}) => {
    const currentIdentity = isIdentity(identity) ? identity : undefined
    let statusText: string | undefined = undefined

    if (!currentIdentity) {
      return statusText
    }

    if (badge) {
      statusText = statusText
        ? `${statusText} is ${badge}`
        : `${currentIdentity.name} is ${badge}`
    }

    return statusText
  })({ badge: participantBadge, identity: participant.identity })

  return (
    <div
      ref={rootRef}
      className={cx(styles.root, { [styles.rootAnimation]: shouldAnimate })}
      style={assignInlineVars({
        [styles.rootBackgroundColor]: getBackgroundColor(
          theme,
          participant.status,
          false,
        ),
        [styles.rootHoverBackgroundColor]: getBackgroundColor(
          theme,
          participant.status,
          true,
        ),
      })}
      onMouseEnter={() => {
        setIsPopoverOpen(true)
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        setIsPopoverOpen(false)
        setIsHovered(false)
      }}
    >
      {shouldShowPopover ? (
        <Popover targetRef={rootRef} placement="top" withUnderlay={false}>
          <ParticipantInfo
            additionalInfo={invitedBy}
            title={title}
            subtitle={subtitle}
            participant={participant}
          />
        </Popover>
      ) : null}
      <ConnectedFeatureTooltip
        name={'participantHold1'}
        showForUsersCreatedBefore="2023-08-24"
        shouldCloseOnScroll={false}
        shouldCloseOnInteractOutside={false}
        autoFocus={false}
        isDisabled={
          !(call.isGroupCall && isFirstExternal && participant.status === 'active')
        }
        title="Put individual participants on hold"
        description="Now, anyone outside of your workspace can be put on hold."
        placement="left"
        openDelay={1000}
        onClose={() => {
          dismiss('participantHold1', 'participantHold2', 'participantHold3')
        }}
        actions={[
          {
            type: 'primary',
            title: 'Show me',
            onAction: () => {
              dismiss('participantHold1')
              setIsMenuOpen(true)
            },
          },
        ]}
      >
        <div className={styles.participantInfo}>
          <AppAvatar
            identity={participant.identity}
            size={24}
            className={styles.avatar}
            style={assignInlineVars({
              [styles.avatarOpacity]:
                participant.status === 'failed' ||
                participant.status === 'ringing' ||
                call.status === 'reconnecting'
                  ? '0.5'
                  : 'inherit',
              [styles.avatarBoxShadow]: participant.isSpeaking
                ? blurred
                  ? `0 0 0 1px rgb(${theme.palette.overlay.strong})`
                  : participant.isListener
                  ? `0 0 0 1px rgb(${theme.palette.primary.default})`
                  : `0 0 0 1px rgb(${theme.palette.secondary.green.default})`
                : '',
            })}
            badge={participantBadge}
          />
          {participantStatus && <VisuallyHidden>{participantStatus}</VisuallyHidden>}
          <div className={styles.nameContainer}>
            <Typography
              variant="callout"
              className={styles.name}
              style={assignInlineVars({
                [styles.nameColor]:
                  participant.status === 'failed'
                    ? `rgb(${theme.palette.secondary.red.default})`
                    : participant.status === 'ringing' ||
                      call.status === 'reconnecting' ||
                      participant.isListener ||
                      participant.isMuted ||
                      participant.onHold
                    ? `rgb(${theme.palette.gray[2]})`
                    : `rgb(${theme.palette.gray[1]})`,
                [styles.nameOpacity]:
                  participant.status === 'failed'
                    ? '0.8'
                    : participant.status === 'ringing' ||
                      call.status === 'reconnecting' ||
                      participant.isListener
                    ? '0.75'
                    : 'inherit',
              })}
            >
              <TextWrap value={name}>{name}</TextWrap>
            </Typography>
            {participant.isCurrentUser ? (
              <Typography variant="callout" color="textTertiary" className={styles.label}>
                You
              </Typography>
            ) : null}
            {participant.status === 'ringing' || call.status === 'reconnecting' ? (
              <LoadingIndicator
                className={styles.progress}
                size={14}
                variant="circular"
              />
            ) : viaSuffix ? (
              <Typography variant="callout" color="textTertiary" className={styles.label}>
                <TextWrap value={viaSuffix}>{viaSuffix} </TextWrap>
              </Typography>
            ) : null}
            {participant.isSpeaking && call.status !== 'reconnecting' ? (
              <div
                className={cx(styles.speakingIndicator, {
                  [styles.speakingIndicatorBlurred]: blurred,
                })}
                style={assignInlineVars({
                  [styles.speakingIndicatorColor]: participant.isListener
                    ? `rgb(${theme.palette.primary.strong})`
                    : `rgb(${theme.palette.secondary.green.default})`,
                })}
              />
            ) : null}
            {call.participants.current?.coaching === participant &&
            call.status !== 'reconnecting' ? (
              <WhisperIcon className={styles.whisper} />
            ) : null}
          </div>
        </div>
      </ConnectedFeatureTooltip>
      {showShowActionButtons ? (
        <div className={styles.buttonContainer}>
          {participant.status === 'ringing' || !participant.status ? (
            <Tooltip
              themeKey="dark"
              title="Canceling will end the call"
              disabled={call.isGroupCall}
              placement="top"
              colorScheme="call"
            >
              {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
              <Button
                height={24}
                color="textSecondary"
                variant="contained"
                className={cx(
                  styles.button,
                  { [styles.buttonBlurred]: blurred },
                  styles.cancelButton,
                )}
                onClick={handleCancel}
              >
                <Typography variant="caption2">Cancel</Typography>
              </Button>
            </Tooltip>
          ) : participant.status === 'active' ? (
            <>
              <Menu.Provider
                isOpen={isMenuOpen}
                targetRef={moreRef}
                onClose={() => setIsMenuOpen(false)}
              >
                <Menu.List onAction={onAction} themeKey="dark" placement="top right">
                  {...menuItems}
                </Menu.List>
              </Menu.Provider>
              {shouldShowMoreActionsMenu ? (
                // eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button
                <IconButton
                  color="gray"
                  ref={moreRef}
                  size={24}
                  icon={<MoreIcon />}
                  onClick={handleMore}
                  title="Actions"
                  tooltip={{
                    themeKey: 'dark',
                    colorScheme: 'call',
                    placement: 'top',
                  }}
                />
              ) : null}
            </>
          ) : participant.status === 'failed' ||
            participant.status === 'no-answer' ||
            participant.status === 'busy' ? (
            <>
              {/* FIXME: The retry button is currently descoped due to backend limitations
              https://linear.app/openphone/issue/VOICE-8120/remove-retry-option-on-the-participant
              <Tooltip title="Call the participant again" themeKey="dark" placement="top" variant="call">
                <Button
                  height={24}
                  color="textSecondary"
                  variant="contained"
                  className={cx(
                    styles.button,
                    { [styles.buttonBlurred]: blurred },
                    styles.retryButton,
                  )}
                  onClick={handleRetry}
                >
                  <Typography variant="caption2">Retry</Typography>
                </Button>
              </Tooltip> */}
              {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
              <IconButton
                title="There was a problem adding this number, please remove and try again"
                tooltip={{
                  themeKey: 'dark',
                  colorScheme: 'call',
                  placement: 'top',
                }}
                size={24}
                icon={<XIcon color={`rgb(${theme.palette.secondary.red.default})`} />}
                hoverColor="red"
                className={cx(styles.removeButton, {
                  [styles.buttonBlurred]: blurred,
                })}
                onClick={handleRemove}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

const getBackgroundColor = (
  theme: ThemeContract,
  status: ActiveCallParticipant['status'],
  isHovered: boolean,
) => {
  if (status === 'failed' || status === 'no-answer' || status === 'busy') {
    if (isHovered) {
      return `rgb(${theme.palette.secondary.red.subtle})`
    } else {
      return `rgb(${theme.palette.secondary.red.strong}, 0.12)`
    }
  }
  if (isHovered) {
    return `rgb(${theme.palette.overlay.default})`
  }

  return 'inherit'
}

export default observer(ParticipantRow)
